import { festivalMinutes } from '@shared/dayCutoff'
import type { Set as FestivalSet, Stage } from '../types'

// A lock-screen image of one festival day (#658): the schedule's own cards,
// squeezed between the lock screen's clock and its bottom controls. Laid out
// at a logical width of at least 390px — wider when the stages need it, so
// each card keeps the room it has on the schedule — and exported at a fixed
// pixel width, so a busy day zooms out instead of crushing the cards.
const BASE_WIDTH = 390
const OUTPUT_WIDTH = 1170
const MIN_COLUMN = 96
const COLUMN_GAP = 2
const GUTTER = 12
const AXIS = 22
/** The lock-screen clock and widgets take the top of the screen; the grid starts below them. */
const GRID_TOP_FRACTION = 0.33
/** Flashlight / camera buttons and the home indicator, plus a footer line. */
const GRID_BOTTOM_INSET = 88
/** The schedule's own density (SLOT_PX): a sparse day never grows past it. */
const MAX_PX_PER_MIN = 1.7
const DEFAULT_ASPECT = 844 / 390

/**
 * `off`: a plain schedule with none of your picks marked (a base wallpaper).
 * `none`: your picks marked, nothing faded. `interested` / `going`: the rest
 * fades, like the schedule's filter — interested includes going.
 */
export type WallpaperHighlight = 'off' | 'none' | 'interested' | 'going'
export type PickStatus = 'going' | 'interested'

export interface WallpaperOptions {
  day: string
  highlight: WallpaperHighlight
  hiddenStageIds: ReadonlySet<string>
  /** The hours shown, in festival minutes (≥ 1440 = after midnight); absent = the whole day. */
  range?: { start: number; end: number }
}

export interface WallpaperFrame {
  width: number
  height: number
  /** Logical width over the 390px base: everything that isn't a card scales by it. */
  zoom: number
  /** Device-pixel multiplier for the export. */
  scale: number
  /**
   * How much bigger than on the schedule a card draws its contents. A wallpaper
   * can't be zoomed, so names and times get a boost — more on a wide canvas,
   * which the export shrinks the most.
   */
  cardScale: number
  gridTop: number
  gridBottom: number
  gutter: number
  axis: number
}

export interface WallpaperBlock {
  set: FestivalSet
  status: PickStatus | null
  /** Outside the highlight: faded like a filtered-out set on the schedule. */
  dimmed: boolean
  /** The set's own start and end, in festival minutes. */
  setSpan: [number, number]
  /** The part of it the image shows (the set cut to a picked hours range), in festival minutes. */
  span: [number, number]
  top: number
  height: number
  left: number
  width: number
}

export interface WallpaperColumn {
  stage: Stage
  left: number
  width: number
}

export interface WallpaperLayout {
  frame: WallpaperFrame
  columns: WallpaperColumn[]
  blocks: WallpaperBlock[]
  /** Grid-space minutes (≥ 1440 = after midnight) and their y. */
  hours: { minutes: number; top: number }[]
  empty: null | 'no_stages' | 'no_sets'
}

/** The phone's height / width, from the screen it's made on; a desktop gets a typical phone. */
export function wallpaperAspect(screen: { width: number; height: number } | undefined): number {
  if (!screen || screen.width <= 0 || screen.height <= screen.width) return DEFAULT_ASPECT
  return Math.min(2.4, Math.max(1.6, screen.height / screen.width))
}

export function wallpaperPickStatus(
  setId: string,
  interested: ReadonlySet<string>,
  going: ReadonlySet<string>,
): PickStatus | null {
  if (going.has(setId)) return 'going'
  if (interested.has(setId)) return 'interested'
  return null
}

function isHighlighted(status: PickStatus | null, highlight: WallpaperHighlight): boolean {
  if (highlight === 'none' || highlight === 'off') return true
  return highlight === 'going' ? status === 'going' : status !== null
}

function wallpaperFrame(columnCount: number, aspect: number): WallpaperFrame {
  // The gutters and axis zoom with the canvas, so solve for the width that leaves MIN_COLUMN.
  const chrome = (2 * GUTTER + AXIS) / BASE_WIDTH
  const needed = (columnCount * MIN_COLUMN + (columnCount - 1) * COLUMN_GAP) / (1 - chrome)
  const width = Math.ceil(Math.max(BASE_WIDTH, needed))
  const zoom = width / BASE_WIDTH
  const height = Math.round(width * aspect)
  return {
    width,
    height,
    zoom,
    scale: OUTPUT_WIDTH / width,
    cardScale: Math.min(1.5, Math.max(1.3, zoom)),
    gridTop: Math.round(height * GRID_TOP_FRACTION),
    gridBottom: height - GRID_BOTTOM_INSET * zoom,
    gutter: GUTTER * zoom,
    axis: AXIS * zoom,
  }
}

/**
 * The day's hours, whole hours around its first start and last end over every
 * stage, in festival minutes: the bounds of the hours picker. Null on a day
 * with no timed sets.
 */
export function wallpaperDayRange(sets: FestivalSet[], day: string, cutoff: number): { start: number; end: number } | null {
  const spans = sets.filter(s => s.day === day && s.stageId && s.startTime && s.endTime).map(s => span(s, cutoff))
  if (spans.length === 0) return null
  return {
    start: Math.floor(Math.min(...spans.map(s => s[0])) / 60) * 60,
    end: Math.ceil(Math.max(...spans.map(s => s[1])) / 60) * 60,
  }
}

function span(set: FestivalSet, cutoff: number): [number, number] {
  const start = festivalMinutes(set.startTime, cutoff)
  let end = festivalMinutes(set.endTime, cutoff)
  if (end <= start) end += 1440
  return [start, end]
}

export function layoutWallpaper({
  sets,
  stages,
  interestedSetIds,
  goingSetIds,
  options,
  cutoff,
  aspect = DEFAULT_ASPECT,
}: {
  /** Every set of the event; filtered to `options.day` here. */
  sets: FestivalSet[]
  /** In the user's stage order. */
  stages: Stage[]
  interestedSetIds: ReadonlySet<string>
  goingSetIds: ReadonlySet<string>
  options: WallpaperOptions
  cutoff: number
  aspect?: number
}): WallpaperLayout {
  const timed = sets.filter(
    s => s.day === options.day && s.stageId && s.startTime && s.endTime && !options.hiddenStageIds.has(s.stageId),
  )
  if (timed.length === 0) {
    return { frame: wallpaperFrame(1, aspect), columns: [], blocks: [], hours: [], empty: 'no_stages' }
  }
  // Sets that overlap the picked hours; one running across an edge is cut to it.
  const pick = options.range
  const daySets = timed
    .map(set => ({ set, span: span(set, cutoff) }))
    .filter(({ span: [a, b] }) => !pick || (b > pick.start && a < pick.end))
    .map(({ set, span: full }) => ({
      set,
      setSpan: full,
      span: (pick ? [Math.max(full[0], pick.start), Math.min(full[1], pick.end)] : full) as [number, number],
    }))
  const columnStages = stages.filter(st => daySets.some(d => d.set.stageId === st.id))
  const frame = wallpaperFrame(Math.max(1, columnStages.length), aspect)
  if (columnStages.length === 0) return { frame, columns: [], blocks: [], hours: [], empty: 'no_sets' }

  const inner = frame.width - 2 * frame.gutter - frame.axis
  const colWidth = (inner - COLUMN_GAP * (columnStages.length - 1)) / columnStages.length
  const columns = columnStages.map((stage, i) => ({
    stage,
    left: frame.gutter + frame.axis + i * (colWidth + COLUMN_GAP),
    width: colWidth,
  }))
  const leftOf = new Map(columns.map(c => [c.stage.id, c.left]))

  const rangeStart = Math.floor(Math.min(...daySets.map(d => d.span[0])) / 60) * 60
  const rangeEnd = Math.ceil(Math.max(...daySets.map(d => d.span[1])) / 60) * 60
  const pxPerMin = Math.min((frame.gridBottom - frame.gridTop) / (rangeEnd - rangeStart), MAX_PX_PER_MIN)
  const y = (m: number) => frame.gridTop + (m - rangeStart) * pxPerMin

  const blocks = daySets.map(({ set, setSpan, span: [a, b] }) => {
    const status = options.highlight === 'off' ? null : wallpaperPickStatus(set.id, interestedSetIds, goingSetIds)
    return {
      set,
      status,
      dimmed: !isHighlighted(status, options.highlight),
      setSpan,
      span: [a, b] as [number, number],
      top: y(a),
      height: (b - a) * pxPerMin,
      left: leftOf.get(set.stageId!)!,
      width: colWidth,
    }
  })

  const step = pxPerMin * 60 >= 18 * frame.zoom ? 60 : 120
  const hours: WallpaperLayout['hours'] = []
  for (let m = rangeStart; m <= rangeEnd; m += step) hours.push({ minutes: m, top: y(m) })

  return { frame, columns, blocks, hours, empty: null }
}


/**
 * The source rect that `background-size: cover` at `background-position: fx% fy%`
 * shows of an image: what drawImage crops so a card frames a photo exactly as
 * the schedule does (focal point from `focalPosition`, default center top).
 */
export function coverCrop(
  image: { width: number; height: number },
  box: { width: number; height: number },
  focal: { x: number; y: number } = { x: 50, y: 0 },
): { sx: number; sy: number; sw: number; sh: number } {
  const scale = Math.max(box.width / image.width, box.height / image.height)
  const sw = box.width / scale
  const sh = box.height / scale
  return { sx: (image.width - sw) * (focal.x / 100), sy: (image.height - sh) * (focal.y / 100), sw, sh }
}

/**
 * Greedy word wrap into at most `maxLines` lines of `maxWidth`. A word too wide
 * for a line on its own, and the last line when text is left over, end in "…".
 */
export function wrapText(measure: (text: string) => number, text: string, maxWidth: number, maxLines: number): string[] {
  if (maxLines < 1) return []
  const fit = (s: string) => {
    if (measure(s) <= maxWidth) return s
    let cut = s
    while (cut.length > 1 && measure(`${cut}…`) > maxWidth) cut = cut.slice(0, -1)
    return `${cut.trimEnd()}…`
  }
  const words = text.split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let line = ''
  for (let i = 0; i < words.length; i++) {
    const candidate = line ? `${line} ${words[i]}` : words[i]
    if (!line || measure(candidate) <= maxWidth) {
      line = candidate
      continue
    }
    if (lines.length === maxLines - 1) return [...lines, fit(`${line} ${words.slice(i).join(' ')}`)]
    lines.push(fit(line))
    line = words[i]
  }
  if (line) lines.push(fit(line))
  return lines
}

/** `text` wrapped into at most `maxLines` lines of `maxWidth` with nothing cut, or null when it doesn't fit whole. */
export function wrapTextWhole(measure: (text: string) => number, text: string, maxWidth: number, maxLines: number): string[] | null {
  const words = text.split(/\s+/).filter(Boolean)
  if (maxLines < 1 || words.some(w => measure(w) > maxWidth)) return null
  const lines: string[] = []
  let line = ''
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word
    if (!line || measure(candidate) <= maxWidth) {
      line = candidate
      continue
    }
    lines.push(line)
    line = word
  }
  if (line) lines.push(line)
  return lines.length <= maxLines ? lines : null
}
